import { Form, Popup } from '@approvalmax/ui/src/components';
import debounce from 'lodash/debounce';
import { selectors } from 'modules/common';
import { domain, State } from 'modules/data';
import { useDispatch } from 'modules/react-redux';
import { useProgressLoading } from 'modules/utils';
import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useToggle, useUnmount } from 'react-use';
import { AddBillInvoicesPopup, FeatureBadge } from 'shared/components';
import { useFilters } from 'shared/components/AddBillInvoicesPopup/AddBillInvoicesPopup.hooks';

import { editRequestField, updateXeroBPDetails } from '../../../actions';
import { RequesterInstructionSection } from '../../../components';
import { RequestEditMode } from '../../../selectors/requestSelectors';
import { getXeroContext } from '../../../selectors/xero';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import BillsInformation from './components/BillsInformation/BillsInformation';
import MainInformation from './components/MainInformation/MainInformation';
import { createMapFilterTransferWrapper } from './XeroBillBatchPaymentForm.helpers';
import { useBillsForBatchPaymentData } from './XeroBillBatchPaymentForm.hooks';
import { messages } from './XeroBillBatchPaymentForm.messages';
import {
    Request,
    Root,
    Toolbar,
    ToolbarHeader,
    ToolbarHeaderText,
    ToolbarHeaderTextContainer,
} from './XeroBillBatchPaymentForm.styles';
import { FilterTrasferData } from './XeroBillBatchPaymentForm.types';
import { XeroBillBatchPaymentBaseProps, XeroBillBatchPaymentFormProps } from './XeroBillBatchPaymentForm.types';
import {
    useXeroBillBatchPaymentRequestContext,
    XeroBillBatchPaymentRequestContextProvider,
} from './XeroBillBatchPaymentRequestContext';

const XeroBillBatchPaymentBase: FC<XeroBillBatchPaymentBaseProps> = (props) => {
    const { request } = props;

    const dispatch = useDispatch();
    const xeroContext = useSelector(getXeroContext);
    const company = useSelector((state: State) => selectors.company.getCompanyById(state, request.companyId));
    const showPremiumBadge = company.flags.isAllFeaturesTrialActive || company.flags.isTrialLicense;
    const [isOpenAddBillsPopup, toggleIsOpenAddBillsPopup] = useToggle(false);
    const integration = useSelector((state: State) =>
        selectors.integration.getIntegrationByCompanyId(state, request.companyId)
    );
    const {
        billBatchPayment,
        addBills,
        updateBillBatchPayment,
        saveAndSubmit,
        setShowErrors,
        isSaving,
        integrationCountryCode,
        editMode,
    } = useXeroBillBatchPaymentRequestContext();

    const mapFilterTransferWithRequestId = useMemo(() => createMapFilterTransferWrapper(request.id), [request.id]);

    const {
        initialTransferFilterData,
        sortingConfig,
        filterTransfer,
        setFilterTransfer,
        changeSortingConfig,
        applyFilters,
        resetFilter,
        form: filtersForm,
    } = useFilters<FilterTrasferData>(mapFilterTransferWithRequestId);

    const { billsForBatchPaymentData, billsForBatchPaymentLoading } = useBillsForBatchPaymentData({
        companyId: request.companyId,
        integrationCountryCode,
        filterTransfer,
        enabled: editMode !== RequestEditMode.Approver && isOpenAddBillsPopup,
    });

    useEffect(() => {
        updateBillBatchPayment(request.details);
    }, [request.details, updateBillBatchPayment]);

    useUnmount(() => {
        dispatch(updateXeroBPDetails(billBatchPayment));
    });

    useProgressLoading(isSaving);

    const handleSubmitFilters = filtersForm.handleSubmit(applyFilters);

    const handleAddBills = useCallback(
        (selectableBills: domain.XeroBatchSelectableBillItem[]) => {
            addBills(selectableBills);

            filtersForm.reset();
            setFilterTransfer(initialTransferFilterData);
            toggleIsOpenAddBillsPopup();
        },
        [addBills, filtersForm, initialTransferFilterData, setFilterTransfer, toggleIsOpenAddBillsPopup]
    );

    const onSaveAndSubmit = useCallback(
        (commentText: string) => {
            setShowErrors(true);
            saveAndSubmit(commentText);
        },
        [saveAndSubmit, setShowErrors]
    );

    return (
        <Root>
            <Toolbar>
                <ToolbarHeader>
                    <ToolbarHeaderTextContainer>
                        <ToolbarHeaderText>{messages.headerText}</ToolbarHeaderText>

                        {showPremiumBadge && <FeatureBadge premium />}
                    </ToolbarHeaderTextContainer>

                    <ActionsBlock customSaveAndSubmitRequest={onSaveAndSubmit} isSaving={isSaving} />
                </ToolbarHeader>

                <MainInformation countryCode={integration!.countryCode} />
            </Toolbar>

            <Request>
                <RequesterInstructionSection instructionText={xeroContext.requesterInstruction} />

                <BillsInformation
                    request={request}
                    toggleIsOpenAddBillsPopup={toggleIsOpenAddBillsPopup}
                    countryCode={integration!.countryCode}
                />
            </Request>

            <Popup open={isOpenAddBillsPopup} onToggle={toggleIsOpenAddBillsPopup} size='large' qa='add-bill-invoices'>
                <Form form={filtersForm} onSubmit={handleSubmitFilters}>
                    <AddBillInvoicesPopup
                        billBatchPayment={billBatchPayment}
                        companyId={request.companyId}
                        requestId={request.id}
                        data={billsForBatchPaymentData}
                        isLoading={billsForBatchPaymentLoading}
                        resetFilter={resetFilter}
                        sortingConfig={sortingConfig}
                        changeSortingConfig={changeSortingConfig}
                        handleAddBills={handleAddBills}
                        attentionText={messages.addBillsAttentionText}
                        showTotalAmountOfSelectedBills
                    />
                </Form>
            </Popup>
        </Root>
    );
};

const XeroBillBatchPaymentForm: FC<XeroBillBatchPaymentFormProps> = memo((props) => {
    const { request } = props;

    const dispatch = useDispatch();

    const debouncedEditRequestField = debounce(
        <TDetails, TKey extends keyof TDetails = keyof TDetails>(propName: TKey, newValue: TDetails[TKey]) => {
            dispatch(editRequestField<TDetails>(propName, newValue));
        },
        200
    );

    return (
        <XeroBillBatchPaymentRequestContextProvider request={request} editRequestField={debouncedEditRequestField}>
            <XeroBillBatchPaymentBase request={request} />
        </XeroBillBatchPaymentRequestContextProvider>
    );
});

export default XeroBillBatchPaymentForm;
